import React from 'react';
import styled from 'styled-components';

import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { FraudReport } from 'fraud/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { useFraudReportStatus } from 'fraud/hooks/useFraudReportStatus';

const t = withLocalePrefix('fraud.fraud_report.status_banner');

const StatusBannerGrid = styled(Grid)`
  border-radius: 5px;
  border: 1px solid ${FullPalette.GREY_600};
  background: ${FullPalette.GREY_E500};
`;

const MissingFieldsList = styled.ul`
  margin: 0;
  padding-left: ${Tokens.spacing['3']};

  li {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }
`;

export const FraudStatusBanner = ({
  fraudReport,
  dataProvides = 'fraud-status-banner',
}: {
  fraudReport: FraudReport | null;
  dataProvides?: string;
}) => {
  const {
    displayStatus,
    isReportPending,
    isReportSuccess,
    isReportStale,
    isReportPartiallyChecked,
  } = useFraudReportStatus(fraudReport);

  if (isReportSuccess && !isReportPartiallyChecked && !isReportStale) {
    return null;
  }

  return (
    <StatusBannerGrid
      container
      spacing={0}
      sx={{ paddingX: 3, paddingY: 2 }}
      alignItems="flex-start"
      flexWrap="nowrap"
      data-provides={dataProvides}
    >
      {isReportPending && (
        <Grid sx={{ marginRight: 1 }}>
          <LoadingSpinner size="small" />
        </Grid>
      )}
      <Stack spacing={0.5}>
        <Body medium>{t(`${displayStatus}.header`)}</Body>
        <SecondaryBody>{t(`${displayStatus}.subheader`)}</SecondaryBody>
        {displayStatus === 'partial_check' && (
          <SecondaryBody as="div">
            <MissingFieldsList data-provides="fraud-status-banner-missing-fields">
              {fraudReport?.digital_profile?.missing_data?.map((field) => (
                <li key={`missing-field-${field}`}>
                  {t(`${displayStatus}.missing_data.${field}`)}
                </li>
              ))}
            </MissingFieldsList>
          </SecondaryBody>
        )}
      </Stack>
    </StatusBannerGrid>
  );
};

export default FraudStatusBanner;
